import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';

import type { ProductDetail } from '../../api/business/types';
import { RELATION_KINDS } from '../../components/relations';
import { useLanguage } from '../../i18n/LanguageContext';

import { RelatedProductsList } from './RelatedProductsList';

type ProductRelationsSectionProps = {
  companyId: string;
  product: ProductDetail;
  canManage: boolean;
};

/**
 * A product's related products (R0030 WP-08) — accessories, up-sells, cross-sells — each kind with the shops that
 * show it: PrestaShop its accessories, WooCommerce its up-sells and cross-sells.
 */
export const ProductRelationsSection = ({ companyId, product, canManage }: ProductRelationsSectionProps) => {
  const { t } = useLanguage();

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6">{t('relations.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('relations.hint')}
      </Typography>
      {RELATION_KINDS.map((kind, index) => (
        <Fragment key={kind}>
          {index > 0 ? <Divider sx={{ my: 2 }} /> : null}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1">{t(`relations.kind.${kind}`)}</Typography>
              <Chip size="small" variant="outlined" label={t(`relations.shops.${kind}`)} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t(`relations.kindHint.${kind}`)}
            </Typography>
            <RelatedProductsList
              companyId={companyId}
              productId={product.id}
              kind={kind}
              related={product.relations[kind]}
              canManage={canManage}
            />
          </Box>
        </Fragment>
      ))}
    </Paper>
  );
};
